import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { HiPlus } from 'react-icons/hi';
import Swal from 'sweetalert2';
import AreaSelector from '../modules/Control/AreaSelector';
import PostList from '../modules/Control/PostList';
import BudgetSummary from '../modules/Control/BudgetSummary';
import PostModal from '../modules/Control/PostModal';
import BudgetPieChart from '../modules/Control/BudgetPieChart';
import AreaDetailPieChart from '../modules/Control/AreaDetailPieChart';
import usePostManagement from '../modules/Control/usePostManagement';
import useBudgetManagement from '../modules/Control/useBudgetManagement';
import styles from '../css/modal.css';
import '../css/ControlPage.css';

export default function ControlPage() {
  const [selectedArea, setSelectedArea] = useState(null);
  const location = useLocation();
  const tableRef = useRef(null);

  const {
    posts,
    sortConfig,
    handleSort,
    searchTerm,
    handleSearch,
    modalIsOpen,
    setModalIsOpen,
    editingPost,
    postFormData,
    setPostFormData,
    handleAddPost,
    handleEditPost,
    handlePostSubmit,
    handleDeletePost,
  } = usePostManagement(selectedArea);

  const {
    budgets,
    calculateBudgetUsage,
    formatCurrency,
    updateUsedBudget,
  } = useBudgetManagement(selectedArea, posts);

  useEffect(() => {
    if (location.state && location.state.selectedArea) {
      const area = location.state.specificArea || location.state.selectedArea;
      setSelectedArea(area.toLowerCase());
    }
  }, [location]);

  useEffect(() => {
    if (selectedArea && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedArea]);

  useEffect(() => {
    if (selectedArea) {
      const { totalCost } = calculateBudgetUsage(selectedArea);
      updateUsedBudget(selectedArea, totalCost);
    }
  }, [selectedArea, posts, calculateBudgetUsage, updateUsedBudget]);

  const budgetData = selectedArea ? calculateBudgetUsage(selectedArea) : null;

  const handleOpenModal = () => setModalIsOpen(true);
  const handleCloseModal = () => setModalIsOpen(false);

  return (
    <div className="test">
      <AreaSelector
        onAreaSelect={setSelectedArea}
        selectedArea={selectedArea}
        className={styles.areaSelector}
        buttonClassName={styles.areaButton}
      />
      <div className={styles.controlView}>
        <div className="profit-page__budget-charts-panels">
          <div className="profit-page__area-charts-grid">
            <AreaDetailPieChart formatCurrency={formatCurrency} />
          </div>
        </div>
        {selectedArea && (
          <div ref={tableRef} className={styles.areaPosts}>
            <div className="areaName-container">
              <h3 className="areaName">Control</h3>
              <span className="areaName-underline">
                {selectedArea.charAt(0).toUpperCase() + selectedArea.slice(1)}
              </span>
            </div>
            <div className="addbtn-container">
              <button className="addPost" onClick={handleOpenModal}>
                <HiPlus size={20} style={{ marginRight: '0.5rem' }} />
                Añadir
              </button>
            </div>
            <div className={styles.postList}>
              <PostList
                posts={posts}
                onSort={handleSort}
                sortConfig={sortConfig}
                onSearch={handleSearch}
                searchTerm={searchTerm}
                onEdit={handleEditPost}
                onDelete={handleDeletePost}
                formatCurrency={formatCurrency}
                editButtonClassName={`${styles.actionButton} ${styles.editButton}`}
                deleteButtonClassName={`${styles.actionButton} ${styles.deleteButton}`}
              />
            </div>
            <div className={styles.budgetSummary}>
              {budgetData && (
                <BudgetSummary
                  budgetData={budgetData}
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
            <div className={styles.budgetPieChart}>
              {budgetData && (
                <BudgetPieChart
                  posts={posts}
                  budgetData={budgetData}
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
          </div>
        )}
        <PostModal
          isOpen={modalIsOpen}
          onClose={handleCloseModal}
          onSubmit={handlePostSubmit}
          postData={postFormData}
          setPostData={setPostFormData}
          isEditing={!!editingPost}
        />
      </div>
    </div>
  );
}

